import { useState, useTransition } from "react";
import { Button } from "../../button";
import { ProvenanceChip, type Provenance } from "../provenance-chip";

/**
 * Per-field provenance metadata (Phase C of
 * docs/onboarding/INPUT-REDUCTION-AUDIT.md). When provided, a brief surface
 * renders a ProvenanceChip + "Looks right / Wrong" actions for any field whose
 * meta.source != "user" and that hasn't been explicitly confirmed yet.
 */
export interface BriefFieldMeta {
  source: Provenance["source"];
  source_url?: string | null;
  selector?: string | null;
  confidence?: number | null;
  confirmed_at?: string | null;
  rejected_at?: string | null;
  source_provider?: string | null;
}

/**
 * Inline row beneath a brief field input that shows where the value came from
 * and (when actionable) lets the user confirm or reject it in one tap. Rendered
 * only when meta exists AND the value hasn't already been confirmed AND the
 * source isn't 'user' (no point showing "from you, OK?").
 *
 * Shared by BriefCarousel and the SetupDriver-based brief walk so the
 * confirm/reject behavior can't drift between the two surfaces.
 */
export function ProvenanceRow({
  fieldKey,
  meta,
  onConfirm,
  onReject,
}: {
  fieldKey: string;
  meta: BriefFieldMeta | null;
  onConfirm?: (key: string) => Promise<void>;
  onReject?: (key: string) => Promise<void>;
}) {
  const [busy, startBusy] = useTransition();
  const [confirmedNow, setConfirmedNow] = useState(false);
  const [rejectedNow, setRejectedNow] = useState(false);

  if (!meta) return null;
  if (meta.source === "user") return null;

  const wasConfirmed = Boolean(meta.confirmed_at) || confirmedNow;
  const wasRejected = Boolean(meta.rejected_at) || rejectedNow;

  const provenance: Provenance = {
    source: meta.source,
    url: meta.source_url ?? undefined,
    selector: meta.selector ?? undefined,
    confidence: meta.confidence ?? undefined,
  };

  return (
    <div className="flex items-center justify-between gap-2 type-subtext text-muted-foreground">
      <ProvenanceChip provenance={provenance} />
      <div className="flex items-center gap-2">
        {wasConfirmed ? (
          <span className="text-foreground" aria-live="polite">
            ✓ Confirmed
          </span>
        ) : wasRejected ? (
          <span className="text-muted-foreground italic" aria-live="polite">
            Rejected — type the right value above
          </span>
        ) : (
          <>
            {onConfirm ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() =>
                  startBusy(async () => {
                    try {
                      await onConfirm(fieldKey);
                      setConfirmedNow(true);
                    } catch {
                      // best-effort; the failed-save UI in the parent already
                      // surfaces server errors via the existing onSave path.
                    }
                  })
                }
                title="Mark this value as correct"
              >
                Looks right
              </Button>
            ) : null}
            {onReject ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() =>
                  startBusy(async () => {
                    try {
                      await onReject(fieldKey);
                      setRejectedNow(true);
                    } catch {
                      /* same rationale as above */
                    }
                  })
                }
                title="Clear this value — the auto-fill was wrong"
              >
                Wrong
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
